import React, { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, Search, Loader2, Phone, Mail } from "lucide-react";
import { VoiceSearchButton, appendSearch } from "@/components/VoiceSearchButton";
import { toast } from "sonner";

const empty = { denominazione: "", tipologia: "Ente pubblico", comune: "", provincia: "", telefono: "", email: "", pec: "", note: "" };
const TIPI = ["Comune", "Cooperativa", "Fondazione", "Associazione", "Azienda", "Scuola", "Università", "Struttura sanitaria", "Studio professionale", "Ente pubblico", "Gruppo informale", "Gruppo familiare", "Altro partner"];

const Organizations = () => {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { setLoading(true); api.get(`/organizations?q=${encodeURIComponent(q)}`).then(({ data }) => setItems(data)).finally(() => setLoading(false)); }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const save = async () => {
    if (!f.denominazione.trim()) { toast.error("Inserisci la denominazione"); return; }
    setSaving(true);
    try { await api.post("/organizations", f); toast.success("Organizzazione creata"); setOpen(false); setF(empty); load(); }
    catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };
  return (
    <div>
      <PageHeader icon={Building2} title="Organizzazioni e partner" description="Comuni, cooperative, scuole, aziende e altri partner"
        actions={can("orgs.write") && <Button onClick={() => { setF(empty); setOpen(true); }} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white"><Plus className="h-4 w-4 mr-2" /> Nuova organizzazione</Button>} />
      <div className="relative mb-4 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca organizzazione…" className="pl-9 pr-11" data-testid="orgs-search" /><div className="absolute right-2 top-1/2 -translate-y-1/2"><VoiceSearchButton onResult={(t) => setQ((p) => appendSearch(p, t))} data-testid="orgs-search-mic" /></div></div>
      {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nessuna organizzazione" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((o) => (
            <Card key={o.id} className="p-4">
              <div className="flex items-start justify-between gap-2"><h3 className="font-medium">{o.denominazione}</h3><span className="text-[10px] rounded-full bg-slate-100 px-2 py-0.5 shrink-0">{o.tipologia}</span></div>
              <div className="text-xs text-muted-foreground mt-1">{o.comune} {o.provincia ? `(${o.provincia})` : ""}</div>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">{o.telefono && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{o.telefono}</span>}{o.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{o.email}</span>}</div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Nuova organizzazione</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Denominazione *</Label><Input value={f.denominazione} onChange={(e) => setF({ ...f, denominazione: e.target.value })} /></div>
            <div><Label>Tipologia</Label><Select value={f.tipologia} onValueChange={(v) => setF({ ...f, tipologia: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent className="max-h-64">{TIPI.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Comune</Label><Input value={f.comune} onChange={(e) => setF({ ...f, comune: e.target.value })} /></div>
            <div><Label>Provincia</Label><Input value={f.provincia} onChange={(e) => setF({ ...f, provincia: e.target.value })} maxLength={2} /></div>
            <div><Label>Telefono</Label><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          </div>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organizations;
